import { useCallback } from 'react';
import { LibraryMusic, Upload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import { Stack, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GenerateIcon } from 'assets/icons';
import { locations } from 'services/routes/locations';
import { analytics, events } from 'services/analytics';
import { ReleaseModalSelection } from 'services/analytics/events/distributionEvents';
import { StyledCloseIcon, StyledModalTitle } from './styled';
import { ReleaseOption } from './components';

interface NewReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenUploadAudioModal: () => void;
}

const NewReleaseModal = ({
  isOpen,
  onClose,
  onOpenUploadAudioModal,
}: NewReleaseModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const onGenerateClick = useCallback(() => {
    analytics.track(
      events.newReleaseModalSelect(ReleaseModalSelection.GENERATE)
    );
    navigate(locations.general.aiMusicGenerator);
  }, [navigate]);

  const onReleaseFromLibraryClick = useCallback(() => {
    analytics.track(
      events.newReleaseModalSelect(ReleaseModalSelection.LIBRARY)
    );
    navigate(locations.library);
  }, [navigate]);

  const onUploadClick = useCallback(() => {
    analytics.track(events.newReleaseModalSelect(ReleaseModalSelection.UPLOAD));
    onOpenUploadAudioModal();
  }, [onOpenUploadAudioModal]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        style: {
          backgroundColor: theme.palette.grey[50],
          padding: theme.spacing(4),
        },
      }}
    >
      <StyledModalTitle variant="h2">
        {t('library:newReleaseModal:modalTitle')}
      </StyledModalTitle>
      <StyledCloseIcon onClick={onClose} />

      <Stack>
        <ReleaseOption
          icon={<GenerateIcon />}
          title={t('library:newReleaseModal:generateTrackTitle')}
          subTitle={t('library:newReleaseModal:generateTrackSubTitle')}
          onClick={onGenerateClick}
        />

        <ReleaseOption
          icon={<LibraryMusic />}
          title={t('library:newReleaseModal:releaseFromLibraryTitle')}
          subTitle={t('library:newReleaseModal:releaseFromLibrarySubTitle')}
          onClick={onReleaseFromLibraryClick}
        />

        <ReleaseOption
          icon={<Upload />}
          title={t('library:newReleaseModal:uploadTitle')}
          subTitle={t('library:newReleaseModal:uploadSubTitle')}
          onClick={onUploadClick}
        />
      </Stack>
    </Dialog>
  );
};

export default NewReleaseModal;
